import { useSendRecording } from 'components/AudioRecorder/hooks/useSendRecording';
import { useRef, useState } from 'react';

const DURATION_TICK_MS = 200;
const DEFAULT_MAX_DURATION = 1000 * 60 * 60;

export function useAudioRecorder(fileType = 'mp3', autoUpload = false) {
  const recorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioBlobRef = useRef(null);
  const { sendRecording } = useSendRecording();

  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const timeoutRef = useRef(null);

  const [active, setActive] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [blob, setBlob] = useState(null);
  const [maxDuration, setMaxDuration] = useState(DEFAULT_MAX_DURATION);
  const [micError, setMicError] = useState('');

  // Lazy — called only when the user first clicks record
  const initializeRecorder = async () => {
    if (recorderRef.current) return; // already initialized

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        throw new Error(
          'Microphone access denied. Please allow microphone access in your browser and try again.',
        );
      }
      if (err.name === 'NotFoundError') {
        throw new Error('No microphone found. Please connect a microphone and try again.');
      }
      throw new Error(`Could not access microphone: ${err.message}`);
    }

    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data) audioChunksRef.current.push(e.data);
    };

    recorder.onstart = () => {
      clearAudio();
      startTimeRef.current = performance.now();
      intervalRef.current = setInterval(updateDuration, DURATION_TICK_MS);
      timeoutRef.current = setTimeout(stop, maxDuration);
    };

    recorder.onstop = () => {
      if (audioChunksRef.current.length > 0) {
        audioBlobRef.current = new Blob(audioChunksRef.current, {
          type: `audio/${fileType}`,
        });
        setBlob(audioBlobRef.current);

        if (autoUpload) trySend();
      } else console.error('No audio chunks in recording');

      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  };

  const toggleRecording = (maxDuration) => {
    if (active) stop();
    else {
      setMaxDuration(maxDuration || DEFAULT_MAX_DURATION);
      startRecording();
    }
  };

  const clearAudio = () => {
    audioChunksRef.current = [];
    audioBlobRef.current = null;
    setBlob(null);
    setDuration(0);
  };

  const updateDuration = () => {
    const timeSinceStart = performance.now() - startTimeRef.current;
    setDuration(timeSinceStart);
  };

  const startRecording = async () => {
    setMicError('');
    try {
      await initializeRecorder();
    } catch (err) {
      setMicError(err.message);
      return;
    }

    setActive(true);
    setDuration(0);
    recorderRef.current.start();
  };

  const stop = () => {
    setActive(false);

    recorderRef.current.stop();
  };

  const replayAudio = () => {
    if (!audioBlobRef.current) {
      setReplaying(false);
      return;
    }

    const audioURL = URL.createObjectURL(audioBlobRef.current);

    return <audio src={audioURL} autoPlay />;
  };

  const trySend = () => {
    if (!audioBlobRef.current) return;
    sendRecording(0, audioBlobRef.current, fileType);
  };

  return {
    blob,
    recording: active,
    duration,
    replaying,
    micError,
    setReplaying,
    toggleRecording,
    replayAudio,
    trySend,
    clearAudio,
  };
}
